import React, { useState } from 'react';
import { Download, Trash2, Wifi, WifiOff, HardDrive, FileText, Music, CheckCircle, Loader2, AlertCircle } from 'lucide-react';
import useOfflineContent from '../hooks/useOfflineContent';

const formatSize = (bytes) => {
  if (!bytes) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const TYPE_ICONS = { pdf: FileText, audio: Music, image: FileText };

// Reusable download button for any content
export const DownloadButton = ({ item, compact = false }) => {
  const { downloadContent, removeContent, isDownloaded, downloading } = useOfflineContent();
  const downloaded = isDownloaded(item.id);
  const status = downloading[item.id];

  if (status?.status === 'downloading') {
    return (
      <button disabled className={`flex items-center gap-1.5 ${compact ? 'p-1.5' : 'px-3 py-1.5'} bg-orange-500/10 text-orange-500 rounded-lg text-xs`}>
        <Loader2 size={14} className="animate-spin" /> {compact ? '' : 'Descargando...'}
      </button>
    );
  }

  if (downloaded) {
    return (
      <button onClick={() => removeContent(item.id)} data-testid={`remove-offline-${item.id}`}
        className={`flex items-center gap-1.5 ${compact ? 'p-1.5' : 'px-3 py-1.5'} bg-green-500/10 text-green-500 hover:bg-red-500/10 hover:text-red-400 rounded-lg text-xs transition-colors group`}>
        <CheckCircle size={14} className="group-hover:hidden" />
        <Trash2 size={14} className="hidden group-hover:block" />
        {compact ? '' : <span className="group-hover:hidden">Descargado</span>}
        {compact ? '' : <span className="hidden group-hover:block">Eliminar</span>}
      </button>
    );
  }

  return (
    <button onClick={() => downloadContent(item)} data-testid={`download-offline-${item.id}`}
      className={`flex items-center gap-1.5 ${compact ? 'p-1.5' : 'px-3 py-1.5'} bg-zinc-800 hover:bg-zinc-700 text-zinc-400 hover:text-white rounded-lg text-xs transition-colors`}>
      <Download size={14} /> {compact ? '' : 'Descargar'}
    </button>
  );
};

// Offline indicator bar
export const OfflineIndicator = () => {
  const { isOnline } = useOfflineContent();
  if (isOnline) return null;

  return (
    <div className="fixed top-0 left-0 right-0 z-[300] bg-red-600 text-white text-center py-1.5 text-xs font-medium flex items-center justify-center gap-2"
      data-testid="offline-indicator">
      <WifiOff size={14} /> Sin conexion — Mostrando contenido guardado
    </div>
  );
};

// Full offline content manager panel
const OfflineContentManager = () => {
  const { isOnline, downloads, getStorageUsed, clearAll, removeContent } = useOfflineContent();
  const [expanded, setExpanded] = useState(false);
  const storageUsed = getStorageUsed();

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5" data-testid="offline-manager">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-white font-bold text-sm flex items-center gap-2">
          <HardDrive size={16} className="text-orange-500" /> Contenido Offline
        </h3>
        <div className="flex items-center gap-2">
          {isOnline
            ? <span className="flex items-center gap-1 text-green-500 text-xs"><Wifi size={12} /> Online</span>
            : <span className="flex items-center gap-1 text-red-400 text-xs"><WifiOff size={12} /> Offline</span>}
        </div>
      </div>

      {/* Storage bar */}
      <div className="mb-3">
        <div className="flex items-center justify-between text-xs text-zinc-500 mb-1">
          <span>{downloads.length} archivos guardados</span>
          <span>{formatSize(storageUsed)}</span>
        </div>
        <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden">
          <div className="h-full bg-orange-500 rounded-full transition-all" style={{ width: `${Math.min(100, (storageUsed / (100 * 1024 * 1024)) * 100)}%` }} />
        </div>
      </div>

      {downloads.length === 0 ? (
        <p className="text-zinc-500 text-xs text-center py-4">
          Descarga workbooks y audios para acceder sin conexion
        </p>
      ) : (
        <>
          {/* Downloaded items list */}
          <div className={`space-y-2 ${expanded ? '' : 'max-h-32 overflow-hidden'}`}>
            {downloads.map((d) => {
              const Icon = TYPE_ICONS[d.type] || FileText;
              return (
                <div key={d.id} className="flex items-center gap-3 p-2 bg-zinc-800/50 rounded-lg">
                  <Icon size={16} className="text-orange-500 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-xs truncate">{d.title}</p>
                    <p className="text-zinc-600 text-[10px]">{formatSize(d.size)}</p>
                  </div>
                  <button onClick={() => removeContent(d.id)} className="p-1 text-zinc-500 hover:text-red-400 transition-colors">
                    <Trash2 size={13} />
                  </button>
                </div>
              );
            })}
          </div>

          {downloads.length > 3 && (
            <button onClick={() => setExpanded(!expanded)} className="w-full text-center text-xs text-zinc-500 hover:text-zinc-300 mt-2 transition-colors">
              {expanded ? 'Ver menos' : `Ver todos (${downloads.length})`}
            </button>
          )}

          {/* Clear all */}
          <button onClick={clearAll} className="w-full mt-3 py-2 text-xs text-red-400 hover:bg-red-500/10 rounded-lg transition-colors flex items-center justify-center gap-1">
            <Trash2 size={12} /> Eliminar todo
          </button>
        </>
      )}
    </div>
  );
};

export default OfflineContentManager;
